import { useState, useRef, useMemo } from 'react';
import SVG from 'react-inlinesvg';
import Fuse from 'fuse.js';

import plusSvg from 'assets/icons/plus.svg';
import selectListSvg from 'assets/icons/select_list.svg';
import IconButton from 'lib/IconButton';
import { Theme } from 'lib/IconButton/IconButton.types';
import Popup, { PopupAlign, PopupPosition } from 'lib/Popup';
import Search from 'lib/Search';
import { groupItemsAlphabetically, sortAndSelectItems } from './utils';
import { MultiSelectItem, MultiSelectProps } from './MultiSelect.types';

const MultiSelect = ({
  items = [],
  dataTestId = '',
  selectedItems = [],
  isDisabled = false,
  containerPosition = PopupPosition.BOTTOM,
  onSelect,
  onClearAll,
}: MultiSelectProps): React.ReactElement => {
  const [search, setSearch] = useState('');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const parentRef = useRef<HTMLDivElement>(null);

  const allItems = useMemo(() => sortAndSelectItems(items, selectedItems), [items, selectedItems]);

  const filteredItems = useMemo(() => {
    const fuse = new Fuse(allItems, { keys: ['name'] });
    const results = fuse.search(search).map((i) => i.item);
    return results.length ? results : allItems;
  }, [search, allItems]);

  const handleSelect = (e: React.MouseEvent<HTMLInputElement>): void => {
    const { value } = e.target as HTMLInputElement;

    if (selectedItems.find((i) => i.code === value)) {
      onSelect(selectedItems.filter((i) => i.code !== value));
      return;
    }

    const itemToAdd = items.find((i) => i.code === value);

    if (itemToAdd) {
      onSelect([...selectedItems, itemToAdd]);
    }
  };

  const getLabelClassNames = (item: MultiSelectItem): string => {
    const classNames = [];

    if (!item.disabled) classNames.push('cursor-pointer');

    if (item.selected) {
      classNames.push('bg-primary text-neutral-100 hover:opacity-80');
    } else if (item.disabled) {
      classNames.push('opacity-60 hover:bg-transparent cursor-not-allowed');
    } else {
      classNames.push('hover:bg-neutral-200 hover:text-primary-950');
    }

    return classNames.join(' ');
  };

  return (
    <div ref={parentRef} className="relative">
      <Popup
        parentElementRef={parentRef.current}
        isOpen={isPopupOpen}
        alignContent={PopupAlign.START}
        onClickOutside={() => setIsPopupOpen(false)}
        containerClassName="z-10"
        containerPosition={containerPosition}
        content={
          <div className="bg-neutral-50 rounded-xl shadow-xl p-4">
            <div className="flex mb-3">
              <Search value={search} placeholder="Search..." onChange={(value) => setSearch(String(value ?? ''))} />
              <span className="ml-2">
                <IconButton
                  icon={selectListSvg}
                  name="multiselect-clear-button"
                  theme={Theme.PLAIN}
                  handleOnClick={onClearAll}
                  className="fill-current"
                  dataTestId="multiselect-clear-button"
                />
              </span>
            </div>
            <div className="h-80 overflow-scroll" aria-label="multi-select-list">
              {groupItemsAlphabetically(filteredItems).map((group) => (
                <div key={group[0]}>
                  {!search && <p className="p-2 text-neutral-900 section-header">{group[0].toUpperCase()}</p>}

                  {group[1].map((item) => (
                    <label
                      key={item.code}
                      className={`block p-2 mb-0.5 body-base rounded-md ${getLabelClassNames(item)}`}
                    >
                      <input
                        data-test-id={`multiselect-input-${item.code}`}
                        className="hidden"
                        type="checkbox"
                        onClick={handleSelect}
                        value={item.code}
                        disabled={!!item.disabled}
                      />
                      <span>{item.name}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          </div>
        }
      >
        <button
          data-test-id={dataTestId}
          type="button"
          className="border border-solid text-center border-neutral-300 rounded-md p-2 hover:bg-neutral-100"
          onClick={() => setIsPopupOpen((prevState) => !prevState)}
          disabled={isDisabled}
        >
          <SVG className="fill-current text-neutral-950-opacity-50 w-4 h-4" src={plusSvg} />
        </button>
      </Popup>
    </div>
  );
};

export default MultiSelect;
